import { useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Plus, Pencil, Trash2, Users, KeyRound, Mail } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useUsers, useDeleteUser, useResetUserPassword, useResendUserInvite } from '@/hooks'
import { PageHeader, EmptyState, DataTable, SortableHeader, ConfirmDialog } from '@/components'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { User, Role } from '@/types'
import { UserFormDialog } from './UserFormDialog'
import { InviteStatusBadge } from './InviteStatusBadge'

const roleLabels: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  employee: 'Employee',
  viewer: 'Viewer',
}

const roleVariants: Record<Role, 'default' | 'secondary' | 'outline'> = {
  super_admin: 'default',
  admin: 'default',
  employee: 'secondary',
  viewer: 'outline',
}

export function UsersPage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const { data: users, isLoading, error } = useUsers(effectiveOrgId)
  const deleteMutation = useDeleteUser(effectiveOrgId)
  const resetPasswordMutation = useResetUserPassword()
  const resendInviteMutation = useResendUserInvite()

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [deleteUser, setDeleteUser] = useState<User | null>(null)
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null)
  const [resendInviteUser, setResendInviteUser] = useState<User | null>(null)

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin'

  // Super admin with no org selected
  if (user?.role === 'super_admin' && !effectiveOrgId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Users"
          description="Manage users in your organization"
        />
        <EmptyState
          icon={<Users className="h-12 w-12" />}
          title="No Organization Selected"
          description="Select an organization using the switcher in the header to view users."
        />
      </div>
    )
  }

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column}>Name</SortableHeader>
      ),
      cell: ({ row }) => {
        const rowUser = row.original
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{rowUser.name || '—'}</span>
            <InviteStatusBadge status={rowUser.invite_status} />
          </div>
        )
      },
    },
    {
      accessorKey: 'email',
      header: ({ column }) => (
        <SortableHeader column={column}>Email</SortableHeader>
      ),
    },
    {
      accessorKey: 'role',
      header: ({ column }) => (
        <SortableHeader column={column}>Role</SortableHeader>
      ),
      cell: ({ row }) => {
        const role = row.getValue('role') as Role
        return (
          <Badge variant={roleVariants[role]}>
            {roleLabels[role]}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Created</SortableHeader>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return date.toLocaleDateString()
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const rowUser = row.original
        // Can't edit/delete yourself or users with higher/equal role
        const canModify = canEdit && rowUser.id !== user?.id && (
          user?.role === 'super_admin' || rowUser.role !== 'super_admin'
        )

        if (!canModify) return null

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditUser(rowUser)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              {(rowUser.invite_status === 'active' ||
                rowUser.invite_status === 'reset_required') && (
                <DropdownMenuItem onClick={() => setResetPasswordUser(rowUser)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  Reset Password
                </DropdownMenuItem>
              )}
              {rowUser.invite_status === 'invited' && (
                <DropdownMenuItem onClick={() => setResendInviteUser(rowUser)}>
                  <Mail className="mr-2 h-4 w-4" />
                  Resend Invite
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => setDeleteUser(rowUser)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  if (error) {
    return (
      <div className="text-destructive">
        Failed to load users: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Manage users in your organization"
        actions={
          canEdit && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          )
        }
      />

      {isLoading ? (
        <div className="text-muted-foreground">Loading users...</div>
      ) : users && users.length > 0 ? (
        <DataTable
          columns={columns}
          data={users}
          filterColumn="email"
          filterPlaceholder="Filter by email..."
        />
      ) : (
        <EmptyState
          icon={<Users className="h-12 w-12" />}
          title="No users"
          description="Get started by adding your first user."
          action={
            canEdit && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            )
          }
        />
      )}

      <UserFormDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {editUser && (
        <UserFormDialog
          open={!!editUser}
          onOpenChange={(open) => !open && setEditUser(null)}
          user={editUser}
        />
      )}

      <ConfirmDialog
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
        title="Delete User"
        description={`Are you sure you want to delete "${deleteUser?.name || deleteUser?.email}"? This action cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (deleteUser) {
            deleteMutation.mutate(deleteUser.id, {
              onSuccess: () => setDeleteUser(null),
            })
          }
        }}
        isLoading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={!!resetPasswordUser}
        onOpenChange={(open) => !open && setResetPasswordUser(null)}
        title="Reset Password"
        description={`Are you sure you want to reset the password for "${resetPasswordUser?.name || resetPasswordUser?.email}"? They will receive an email with instructions to set a new password.`}
        confirmText="Reset Password"
        onConfirm={() => {
          if (resetPasswordUser) {
            resetPasswordMutation.mutate(resetPasswordUser.id, {
              onSuccess: () => {
                toast.success('Password reset email sent successfully.')
                setResetPasswordUser(null)
              },
              onError: (error) => {
                toast.error(`Failed to reset password: ${error.message}`)
              },
            })
          }
        }}
        isLoading={resetPasswordMutation.isPending}
      />

      <ConfirmDialog
        open={!!resendInviteUser}
        onOpenChange={(open) => !open && setResendInviteUser(null)}
        title="Resend Invite"
        description={`Are you sure you want to resend the invitation email to "${resendInviteUser?.name || resendInviteUser?.email}"?`}
        confirmText="Resend Invite"
        onConfirm={() => {
          if (resendInviteUser) {
            resendInviteMutation.mutate(resendInviteUser.id, {
              onSuccess: () => {
                toast.success('Invitation email resent successfully.')
                setResendInviteUser(null)
              },
              onError: (error) => {
                toast.error(`Failed to resend invite: ${error.message}`)
              },
            })
          }
        }}
        isLoading={resendInviteMutation.isPending}
      />
    </div>
  )
}
